import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import {
    persistStore,
    persistReducer,
    FLUSH,
    REHYDRATE,
    PAUSE,
    PERSIST,
    PURGE,
    REGISTER,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import logger from 'redux-logger';
import authReducer from './slices/authSlice';
import builderReducer from './slices/builder';
import surveyReducer from './slices/surveySlice';
import previewReducer, { PreviewState } from './slices/preview';
import type { AuthState } from '@/features/auth/types';

// ─── Redux Persist Configuration ──────────────────────────────────
// Only persist auth slice — and only the fields we need for instant UI render.
// The httpOnly cookie IS the session; we persist state to avoid flash of login page.
// On app mount, initializeAuth() always calls GET /auth/me to revalidate.
const authPersistConfig = {
    key: 'auth',
    storage,
    whitelist: ['status', 'user', 'context', 'availableContexts', 'quota'], // NOT token, NOT sessionType
};

// ─── Root State Type ──────────────────────────────────────────────
export interface RootState {
    auth: AuthState;
    preview: PreviewState;
    builder: any; // TODO: Add proper BuilderState type
    survey: {
        pagination: {
            page: number;
            limit: number;
            totalSurveys: number;
            totalPages: number;
        };
        activeSurvey: {
            id: string | null;
            name: string | null;
            status: 'draft' | 'published' | 'archived' | null;
        };
        filters: {
            status: ('draft' | 'published' | 'archived')[];
            search: string;
        };
        analytics: {
            tab: 'analytics' | 'responses'
        }
        liveSurveyCount: number
        createModal: 'create' | 'preview' | null
        channel: 'pps' | 'web' | 'on-site' | 'whatsapp'
    }
}

// ─── Root Reducer ─────────────────────────────────────────────────
const rootReducer = combineReducers({
    auth: persistReducer(authPersistConfig, authReducer),
    preview: previewReducer,
    builder: builderReducer,
    survey: surveyReducer,
});

// ─── Store ────────────────────────────────────────────────────────
export const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) => {
        const middleware = getDefaultMiddleware({
            serializableCheck: {
                // Ignore redux-persist actions for serializable check
                ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
            },
        });
        if (process.env.NODE_ENV === 'development') {
            middleware.push(logger);
        }
        return middleware;
    },
});

// ─── Persistor ────────────────────────────────────────────────────
export const persistor = persistStore(store);

// ─── Typed Hooks ──────────────────────────────────────────────────
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
